import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  MAX_UPLOAD_MB,
  buildUploadFormData,
  classifyUploadFile,
  describeUploadError,
  formatBytes,
  uploadPercent,
  validateUploadForm,
} from "./uploadUtils";

const EMPTY_FORM = {
  appName: "",
  appStoreUrl: "",
  appDescription: "",
  appDeveloper: "",
  appIconUrl: "",
  appRating: "",
  appDownloads: "",
};

const inputClass =
  "max-w-lg block w-full shadow-sm focus:ring-mobstr-purple focus:border-mobstr-purple sm:max-w-xs sm:text-sm border-gray-300 rounded-md";
const rowClass = "sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5";
const labelClass = "block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2";

/**
 * Upload App: side-load an .apk or .crx that is not in a public store. The file is
 * streamed to POST /upload with progress; the API stores it where the scanner
 * looks and queues the app, so it appears in the app list as pending.
 */
export default function ManualUpload() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [progress, setProgress] = useState(null); // null = idle, 0..100 while uploading
  const [dragActive, setDragActive] = useState(false);
  const abortRef = useRef(null);
  const fileInputRef = useRef(null);

  const uploading = progress !== null;
  const fileKind = file ? classifyUploadFile(file.name) : null;

  const setField = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const chooseFile = (chosen) => {
    setFile(chosen || null);
    setServerError("");
    setErrors((prev) => {
      const next = { ...prev };
      delete next.file;
      return next;
    });
  };

  const handleFileChange = (e) => chooseFile(e.target.files && e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (uploading) return;
    chooseFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  const cancelUpload = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const saveApp = async () => {
    if (uploading) return;
    const validation = validateUploadForm({ appName: form.appName, file });
    setErrors(validation);
    setServerError("");
    if (Object.keys(validation).length > 0) return;

    const controller = new AbortController();
    abortRef.current = controller;
    setProgress(0);

    try {
      const token = localStorage.getItem("tokenmob");
      await axios.post(`${process.env.REACT_APP_API_ROOT}/upload`, buildUploadFormData(form, file), {
        headers: { Authorization: "Bearer " + token },
        signal: controller.signal,
        timeout: 0, // a 200 MB file on a slow link takes as long as it takes
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        onUploadProgress: (event) => {
          const pct = uploadPercent(event);
          if (pct !== null) setProgress(pct);
        },
      });
      queryClient.invalidateQueries(["myapps"]);
      setForm(EMPTY_FORM);
      setFile(null);
      navigate("/apps");
    } catch (err) {
      setServerError(describeUploadError(err));
      setProgress(null);
    } finally {
      abortRef.current = null;
    }
  };

  return (
    <>
      <div className="flex justify-between">
        <h2 className="text-xl font-medium">Manually Upload an Application</h2>
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Upload an Android <code>.apk</code> or a Chrome/Edge <code>.crx</code> that is not in a public store (up to{" "}
        {MAX_UPLOAD_MB} MB). The app is queued for scanning straight away and shows in your app list as pending until
        the scan finishes.
      </p>

      {serverError && (
        <div role="alert" className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {serverError}
        </div>
      )}

      <div className="bg-white py-8 px-4 mt-6 shadow sm:rounded-lg sm:px-10 space-y-8 divide-y divide-gray-200">
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:pt-5">
          <label htmlFor="app-name" className={labelClass}>
            App Name *
          </label>
          <div className="mt-1 sm:mt-0 sm:col-span-2">
            <input
              type="text"
              name="app-name"
              id="app-name"
              className={inputClass}
              value={form.appName}
              onChange={setField("appName")}
              disabled={uploading}
              aria-invalid={errors.appName ? "true" : undefined}
              required
            />
            {errors.appName && <p className="mt-2 text-sm text-red-600">{errors.appName}</p>}
          </div>
        </div>

        <div className={rowClass}>
          <span className={labelClass}>Application Binary *</span>
          <div className="mt-1 sm:mt-0 sm:col-span-2">
            <div
              data-testid="dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                if (!uploading) setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              className={`max-w-lg flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${
                dragActive ? "border-mobstr-purple bg-purple-50" : "border-gray-300"
              }`}
            >
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex justify-center text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-mobstr-purple focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-mobstr-purple"
                  >
                    <span>{file ? "Choose a different file" : "Choose a file"}</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept=".apk,.crx"
                      className="sr-only"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      disabled={uploading}
                      aria-label="Application binary"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                {file ? (
                  <p className="text-sm text-gray-700" data-testid="chosen-file">
                    <span className="font-medium">{file.name}</span> · {formatBytes(file.size)}
                    {fileKind ? ` · ${fileKind.label}` : ""}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">apk or crx, up to {MAX_UPLOAD_MB} MB</p>
                )}
              </div>
            </div>
            {errors.file && <p className="mt-2 text-sm text-red-600">{errors.file}</p>}
          </div>
        </div>
      </div>

      <div className="bg-white py-8 px-4 mt-6 shadow sm:rounded-lg sm:px-10 space-y-8 divide-y divide-gray-200">
        <div className="space-y-6 sm:space-y-5">
          <p className="text-sm text-gray-500 sm:pt-5">
            Optional details. There is no store listing for an uploaded app, so what you enter here is what the
            dashboard shows.
          </p>

          <div className={rowClass}>
            <label htmlFor="store-url" className={labelClass}>
              App Store URL
            </label>
            <div className="mt-1 sm:mt-0 sm:col-span-2">
              <div className="max-w-lg flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  https://
                </span>
                <input
                  type="text"
                  name="store-url"
                  id="store-url"
                  value={form.appStoreUrl}
                  onChange={setField("appStoreUrl")}
                  disabled={uploading}
                  className="flex-1 block w-full focus:ring-mobstr-purple focus:border-mobstr-purple min-w-0 rounded-none rounded-r-md sm:text-sm border-gray-300"
                />
              </div>
            </div>
          </div>

          <div className={rowClass}>
            <label htmlFor="description" className={labelClass}>
              Description
            </label>
            <div className="mt-1 sm:mt-0 sm:col-span-2">
              <textarea
                id="description"
                name="description"
                rows={3}
                className="max-w-lg shadow-sm block w-full focus:ring-mobstr-purple focus:border-mobstr-purple sm:text-sm border border-gray-300 rounded-md"
                value={form.appDescription}
                onChange={setField("appDescription")}
                disabled={uploading}
              />
              <p className="mt-2 text-sm text-gray-500">Write a brief description of the app</p>
            </div>
          </div>

          <div className={rowClass}>
            <label htmlFor="app-developer" className={labelClass}>
              Developer/Publisher
            </label>
            <div className="mt-1 sm:mt-0 sm:col-span-2">
              <input
                type="text"
                name="app-developer"
                id="app-developer"
                value={form.appDeveloper}
                onChange={setField("appDeveloper")}
                disabled={uploading}
                className={inputClass}
              />
            </div>
          </div>

          <div className={rowClass}>
            <label htmlFor="icon-url" className={labelClass}>
              Icon URL
            </label>
            <div className="mt-1 sm:mt-0 sm:col-span-2">
              <input
                type="text"
                name="icon-url"
                id="icon-url"
                value={form.appIconUrl}
                onChange={setField("appIconUrl")}
                disabled={uploading}
                className={inputClass}
              />
            </div>
          </div>

          <div className={rowClass}>
            <label htmlFor="app-rating" className={labelClass}>
              Rating
            </label>
            <div className="mt-1 sm:mt-0 sm:col-span-2">
              <input
                type="text"
                name="app-rating"
                id="app-rating"
                value={form.appRating}
                onChange={setField("appRating")}
                disabled={uploading}
                className={inputClass}
              />
            </div>
          </div>

          <div className={rowClass}>
            <label htmlFor="app-downloads" className={labelClass}>
              Downloads
            </label>
            <div className="mt-1 sm:mt-0 sm:col-span-2">
              <input
                type="text"
                name="app-downloads"
                id="app-downloads"
                value={form.appDownloads}
                onChange={setField("appDownloads")}
                disabled={uploading}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      </div>

      {uploading && (
        <div className="mt-6" data-testid="upload-progress">
          <div className="flex justify-between text-sm text-gray-700 mb-1">
            <span>
              Uploading {file ? file.name : ""} · {progress}%
            </span>
            <button type="button" className="text-red-600 hover:underline" onClick={cancelUpload}>
              Cancel
            </button>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
            <div className="bg-mobstr-purple h-2.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-2 text-xs text-gray-500">Keep this page open until the upload completes.</p>
        </div>
      )}

      <div className="pt-5 ">
        <div className="flex justify-end mb-5">
          <button
            type="button"
            className="text-white bg-[#2c4251] hover:bg-[#3d627a] disabled:opacity-60 disabled:cursor-not-allowed font-medium rounded text-sm w-full sm:w-auto px-5 py-2.5 text-center uppercase"
            onClick={saveApp}
            disabled={uploading}
          >
            {uploading ? `Uploading… ${progress}%` : "Upload"}
          </button>
        </div>
      </div>
    </>
  );
}
